import { TeamRole } from '@/consts/team'
import { QuotaType } from '@/consts/team'
import { HardwareInfo, UsageState } from '@/models/instance'

import {
  ServerBillInfo,
  ServerClusterInfo,
  ServerCredentialInfo,
  ServerDeploymentBillInfo,
  ServerDeploymentInfo,
  ServerDomainInfo,
  ServerGatewayInfo,
  ServerImageInfo,
  ServerInstanceInfo,
  ServerItemInfo,
  ServerMachineInfo,
  ServerMeshEndpointInfo,
  ServerMeshEndpointNodeInfo,
  ServerNATInfo,
  ServerPrivateRegionAgentInstance,
  ServerPrivateRegionInfo,
  ServerPrivateRegionLeasedTimeMetric,
  ServerPrivateRegionNodeDetails,
  ServerPrivateRegionNodeHardware,
  ServerPrivateRegionNodeHardwareBrief,
  ServerPrivateRegionNodeSpec,
  ServerPrivateRegionVGpuSpec,
  ServerScaleMetricListInfo,
  ServerScaleSpecInfo,
  ServerScaleSpecListInfo,
  ServerScaleSpecStat,
  ServerSnapInfo,
  ServerSshPubKeyInfo,
  ServerTeamInfo,
  ServerTeamUser,
  ServerTeamVolume,
  Volume,
} from './type-server'

export type LoginRes = {
  token: string
  mfaRequired?: boolean
  mfaChallenge?: string
}

export type TotpStatus = {
  state: number
  recoveryCodeCount: number
}

export type TotpSetup = {
  issuer: string
  account: string
  secret: string
  otpAuthURL: string
  recoveryCodes: string[]
}

export type GetTotpStatusRes = {
  status: TotpStatus
}

export type SetupTotpRes = {
  setup: TotpSetup
}

export type EnableTotpRes = {
  status: TotpStatus
}

export type RegenerateRecoveryCodesRes = {
  recoveryCodes: string[]
  recoveryCodeCount: number
}

export type Pagi<T> = T & {
  pagination: {
    page: number
    perPage: number
    total: number
  }
}

export type GetDomainListRes = Pagi<{ domainInfos: ServerDomainInfo[] | null }>

export type ImageAttributeFilter = {
  key: string
  values: { value: number; valueRaw: string }[]
}

export type GetImageAttributeFiltersRes = { filters: ImageAttributeFilter[] }

export type GetMachineListRes = Pagi<{ machineClasses: ServerMachineInfo[] }>

export type GetInstanceBillsRes = {
  bills: ServerBillInfo[]
  items?: ServerItemInfo[]
}

export type ServerExtraPrice = {
  extraType: number // ExtraPriceType
  priceMillicent: number
  extraCpuInfo?: {
    cpus?: number
    cpuCores?: number
  }
  extraRamInfo?: {
    ramMB?: number
  }
  extraDiskInfo?: {
    extraSizeByte: number
  }
  Info?: {
    ExtraCPUInfo?: {
      cpus?: number
      cpuCores?: number
    }
    ExtraCpuInfo?: {
      cpus?: number
      cpuCores?: number
    }
    ExtraRAMInfo?: {
      ramMB?: number
    }
    ExtraRamInfo?: {
      ramMB?: number
    }
    ExtraDiskInfo?: {
      extraSizeByte: number
    }
  }
}

export type GetMachinePriceRes = {
  priceMillicent: number
  discountPriceMillicent: number
  basePriceMillicent: number
  extraPrices?: ServerExtraPrice[]
}

export type GetImageListRes = Pagi<{ images: ServerImageInfo[] }>

export type GetScaleSpecListRes = Pagi<{ list: ServerScaleSpecListInfo[] }>

export type GetScaleMetricListRes = Pagi<{ matrix: ServerScaleMetricListInfo[] }>

export type GetSnapListRes = Pagi<{ snapshots: ServerSnapInfo[] }>

export type GetMeshListRes = Pagi<{ networks: ServerClusterInfo[] }>

export type GetInstanceListRes = Pagi<{
  userNodes: ServerInstanceInfo[]
}>

export type GetFollowTaskRes = {
  bound: boolean
  followed: boolean
  /** Reward in cents; 100 = 1 credit */
  rewardAmount?: number
}

export type BonusValue = {
  timeSec: number
  moneyCent: number
}

export type BonusStat = {
  rechargeBonus: BonusValue
  giftBonus: BonusValue
  transferBonus: BonusValue
  otherBonus: BonusValue
}

export type GetAccountRes = {
  account: {
    balance: number
    expense: {
      amount: number
      timeSec: number
    }
    timeDiscounts: Array<any>
    name: string
    totalBonus: BonusValue
    bonusStat?: BonusStat
  }
  couponAmountYuan: number
}

// Asset Domain credits transfer (docs/ASSET_DOMAIN_FRONTEND.md)
export type AssetDomain = {
  serviceHostID: number
  alias: string
  hostname: string
}

export type ListAssetDomainsRes = {
  assetDomains: AssetDomain[]
}

export type CreateAssetTransferRes = {
  transfer?: {
    transferID: number
  }
}

export enum AssetTransferStatus {
  Invalid = 0,
  Init = 1,
  Frozen = 2,
  PeerPrepared = 3,
  Committed = 4,
  Finished = 5,
  Cancelling = 6,
  Cancelled = 7,
}

export type AssetTransfer = {
  id: number
  targetAssetDomainAlias: string
  assetType: number
  // Despite the name, the backend returns display cents, not millicents.
  assetMillicent: number
  status: AssetTransferStatus
  createdAt?: { seconds: number; nanos?: number }
  updatedAt?: { seconds: number; nanos?: number }
}

export type ListAssetTransfersRes = {
  pagination?: {
    total: number
    numPages: number
    page: number
    perPage: number
  }
  transfers: AssetTransfer[]
}

export type GetBriefMonitorDataRes = {
  hardware: HardwareInfo
  usageState: UsageState
}

// Detail Monitor Data Types (/mon API)
export type MonitorMatrixEntry = {
  matrix: [number, number] // [timestamp_in_seconds, value]
}

export type MonitorChartResult = {
  name?: string | number
  matrixArray: MonitorMatrixEntry[]
}

export type MonitorChartItem = {
  chartType: number
  result: MonitorChartResult[]
}

export type GetMonitorDataRes = {
  data: MonitorChartItem[]
}

export type GetMeshEndPointListRes = Pagi<{
  endpoints: ServerMeshEndpointInfo[]
}>

export type GetMeshEndpointNodesRes = Pagi<{
  instances: ServerMeshEndpointNodeInfo[]
}>

export type GetScaleSpecRes = {
  spec: ServerScaleSpecInfo
  stats: ServerScaleSpecStat
}

export type GetGatewayListRes = Pagi<{
  gateways: ServerGatewayInfo[]
}>

export type GetDeploymentListRes = Pagi<{
  deployments: ServerDeploymentInfo[]
}>

export type GetDeploymentBillRes = Pagi<{
  billing: ServerDeploymentBillInfo[]
}>

export type GetExchangeRateRes = {
  currency: string
  unit: string
  amount: number
}

export type GetUserSshPubKeyRes = {
  publicKey: ServerSshPubKeyInfo
}

export type GetItemConsumeRes = {
  info: {
    total: number
    used: number
  }
}

export type GetVolumesRes = {
  code: number
  msg?: string
  volumes: Volume[]
}

export type GetMemberVolumesRes = {
  code: number
  msg?: string
  volumes: ServerTeamVolume
}

export type GetNATListRes = Pagi<{
  networks: ServerNATInfo[]
}>

export type GetDonateOrgsRes = {
  organizations: Array<{
    name: string
    donateCode: string
    unifiedCode: string
  }>
  pagination: {
    total: number
    numPages: number
    page: number
    perPage: number
  }
}

export type GetInvoiceTitleRes = {
  invoiceTitle: {
    ezpay: {
      name: string
      type: number
      carrier_type: number
      UBN: string
      email: string
      carrier_num: string
      donate_code: string
    }
  }
}

// Custom Image Responses
export type RegisterImageRes = {
  imageID: number
  storeService: string
}

export type GenerateImageUploadTokenRes = {
  token: string
}

export type UploadImageBlockRes = {
  info: {
    blkWritten: Record<string, boolean>
  }
}

// Private Region Responses
export type GetPrivateRegionInfoRes = {
  infos: ServerPrivateRegionInfo[]
}

export type GetPrivateRegionLeasedTimeMetricsRes = {
  metrics: ServerPrivateRegionLeasedTimeMetric[]
}

export type GetPrivateRegionNodeModelsRes = {
  models: string[]
}

export type GetPrivateRegionNodeHardwareBriefRes = {
  brief: ServerPrivateRegionNodeHardwareBrief
}

export type GetPrivateRegionNodeHardwaresRes = Pagi<{
  hardwares: ServerPrivateRegionNodeHardware[]
}>

export type GetPrivateRegionStatsNodesListRes = Pagi<{
  details: ServerPrivateRegionNodeDetails[]
}>

export type GetPrivateRegionStatsOverviewRes = {
  nodesAll: number
  nodesOnline: number
  leasedTimeHour: number
  leasedTimeHourRateDelta: number
  occupancyRate: number
  occupancyRateDelta: number
}

export type GetPrivateRegionPermissionRes = {
  hasPermission: boolean
}

// Team Responses
export type CreateTeamRes = {
  team: {
    resID: string
    teamName: string
    domainName: string
    adminName: string
    adminPassword: string
  }
}

// Order Response (generic order API)
// detail is null for non-team orders (e.g., space catalog)
// detail.team exists only for team catalog (catalog: 8)
export type CreateOrderRes = {
  detail: {
    team?: {
      teamInfo: {
        resID: string
        teamName: string
        domainName: string
        adminName: string
        adminPassword: string
      }
    }
  } | null
}

// Team Order (subscription info)
export interface ServerTeamOrder {
  autoRenewal: boolean
  createTime: { seconds: number }
  deadlineTime: { seconds: number }
  item: TeamItem
  id: number
  extra: unknown
  renewPrice: ItemPrice
  domain: number
}

export type GetTeamListRes = Pagi<{
  teams: Array<{
    team: ServerTeamInfo
    user: ServerTeamUser
    volumes: ServerTeamVolume | null
    memberStat: TeamMemberStat | null
    order: ServerTeamOrder | null
  }>
}>

export interface TeamMemberStat {
  membersNumLimit: number
  adminsNumLimit: number
  membersCount: number
  adminsCount: number
}

export type GetTeamInfoRes = {
  team: {
    team: ServerTeamInfo
    user: ServerTeamUser
    volumes: ServerTeamVolume
    memberStat: TeamMemberStat
    order: ServerTeamOrder | null
  }
}

export type GetTeamMemberListRes = Pagi<{
  members: Array<{
    resID: string
    user: {
      name: string
      role: number
      nickname: string
      note: string
      bindParent: boolean
      status: number
      lastLoginAt: { seconds: number }
      passwordUpdateAt: { seconds: number }
    }
    account: {
      balance: number
      expense: { amount: number; timeSec: number }
      timeDiscounts: any[]
      name: string
      totalBonus: { timeSec: number; moneyCent: number }
    }
  }>
}>

export type GetTeamMemberInstancesRes = Pagi<{
  instances: Array<{
    user: {
      resID: string
      user: {
        name: string
        role: number
        nickname: string
        note: string
      }
    }
    instance: any // Using ServerUserNode structure
  }>
}>

// Team Snapshot Responses
export type TeamSnapshotWithUser = {
  user: {
    resID: string
    user: {
      name: string
      role: number // 1=Owner, 2=Admin, 3=Normal (see TeamRole in consts/team.ts)
      nickname: string
      note: string
    }
  }
  snapshot: ServerSnapInfo
}

export type GetTeamSharedSnapshotsRes = Pagi<{
  snapshots: TeamSnapshotWithUser[]
}>

export type GetTeamMemberSnapshotsRes = Pagi<{
  snapshots: TeamSnapshotWithUser[]
}>

export type TeamMemberBillWithUser = {
  user: {
    resID: string
    user: {
      name: string
      role: number
      nickname: string
      note: string
    }
  }
  bill: ServerBillInfo & {
    resID: string
  }
}

export type GetTeamMemberBillsRes = {
  code: number
  msg: string
  pagination: {
    total: number
    numPages: number
    page: number
    perPage: number
  }
  bills: TeamMemberBillWithUser[]
}

export type TeamMemberVolumeStat = {
  user: {
    resID: string
    user: {
      name: string
      role: number
      nickname: string
      note: string
    }
  }
  allocPart: {
    usageBytes: number
    quotaGB: number
    regions: number
  }
  snapSubPart: {
    usageBytes: number
    quotaGB: number
    regions: number
  }
  fsSubPart: {
    usageBytes: number
    quotaGB: number
    regions: number
  }
  storageStat: {
    active: boolean
    createTime: { seconds: number } | null
    expireTime: { seconds: number } | null
    autoRenewal: boolean
  }
}

export type GetTeamMemberVolumeStatsRes = Pagi<{
  volumes: TeamMemberVolumeStat[]
}>

// Team Resource ACL Responses
export type QuotaItem = {
  QuotaType: number
  QuotaValue: number
  QuotaValueMax: number
}

export type ResourceACLOptions = {
  enableACL: boolean
  regionOptions: {
    allowedRegions: number[]
  }
  machineOptions: {
    gpuMachineCap: boolean
    cpuMachineCap: boolean
    vgpuMachineCap: boolean
    allowedProcessorModels: string[]
  }
  imageOptions: {
    officialImageCap: boolean
    allowedImages: string[]
  }
}

export type ResourceACLQuotas = {
  items: QuotaItem[]
}

export type ResourceACL = {
  options: ResourceACLOptions
  quotas: ResourceACLQuotas
}

export type GetTeamResourceACLRes = {
  acl: ResourceACL
}

export type GetTeamResourceACLRangesRes = {
  code: number
  msg: string
  aclRanges: {
    options: {
      regionRanges: {
        supported: boolean
        publicRegions: Array<{
          domain: number
          displayName: string
        }>
        privateRegions: Array<{
          domain: number
          displayName: string
        }>
      }
      machineOptions: {
        supported: boolean
        machineCategories: number[]
        machineGPUModels: string[]
        machineCPUModels: string[]
        machineVGPUModels: string[]
      }
      imageOptions: {
        supported: boolean
      }
    }
    quotas: {
      supported: boolean
      quotaTypes: QuotaType[]
    }
  }
}

export type GetTeamMemberResourceACLRes = {
  acl: ResourceACL
}

export interface TeamMemberInfo {
  name: string
  role: TeamRole
  nickname?: string
  note?: string
  bindParent: boolean
  status: number
  lastLoginAt: {
    seconds: number
  }
  passwordUpdateAt: {
    seconds: number
  }
  createdAt: {
    seconds: number
  }
}
export interface TeamMemberAccount {
  balance: number
  expense: {
    amount: number
    timeSec: number
  }
  timeDiscounts: Array<any>
  name: string
  totalBonus: {
    timeSec: number
    moneyCent: number
  }
  bonusStat?: BonusStat
}

export interface GetTeamMemberRes {
  user: TeamMemberInfo
  account: TeamMemberAccount
  volumes: TeamMemberVolume
}

export interface TeamMemberVolume {
  allocPart: {
    usage_mb: number
    quota_gb: number
    init_quota_gb: number
    id: number
    type: number
    domain: number
    res_id: string
    last_quota_gb: number
    usage_bytes: number
  }
  subParts: Array<{
    usage_mb: number
    quota_gb: number
    init_quota_gb: number
    id: number
    type: number
    domain: number
    res_id: string
    last_quota_gb: number
    usage_bytes: number
  }>
}

export interface Pagination {
  total: number
  numPages: number
  page: number
  perPage: number
}

export interface CreateTeamMemberRes {
  member: {
    resID: string
    name: string
    password?: string
    role: TeamRole
  }
}

export interface UpdateTeamMemberPasswordRes {
  password: {
    password: string
  }
}
export interface DistributeCreditRes {
  info: {
    memberCount: number
    eachAmountCent: number
    totalAmountCent: number
  }
}

// Transfer credits from platform to team
export interface TransferCreditsToTeamRes {
  code: number
  msg?: string
}

// Team Statistics - Shared Overview Types
export interface UsageOverview {
  quota: number
  usage: number
}

export interface BytesOverview {
  quotaBytes: number
  usageBytes: number
}

export interface MemberOverview {
  activedMemberNum: number
  inactivedMemberNum: number
  adminRoleNum: number
  memberNumLimit: number
  adminNumLimit: number
}

// Team Statistics Responses
export interface GetTeamStatOverviewRes {
  memberInstanceOverview: UsageOverview
  memberSnapshotOverview: UsageOverview
  teamSharedStorageSpaceOverview: BytesOverview
  teamSharedFSSpaceOverview: BytesOverview
  teamSharedSnapshotSpaceOverview: BytesOverview
}

export interface GetTeamStatInstancesRes {
  memberInstanceOverview: UsageOverview
  snapshotingInstanceNum: number
}

export interface GetTeamStatFSSpacesRes {
  teamSharedFSSpaceOverview: BytesOverview
  memberFSSpaceOverview: BytesOverview
}

export interface GetTeamStatSharedSnapshotsRes {
  teamSharedSnapshotOverview: UsageOverview
  teamSharedSnapshotSpaceOverview: BytesOverview
}

export interface GetTeamStatSnapshotsRes {
  teamSharedSnapshotOverview: UsageOverview
  teamSharedSnapshotSpaceOverview: BytesOverview
  memberSnapshotOverview: UsageOverview
  memberSnapshotSpaceOverview: BytesOverview
}

export interface GetTeamStatStoragesRes {
  teamSharedStorageSpaceOverview: BytesOverview
  memberStorageSpaceOverview: BytesOverview
}

export interface GetTeamStatMembersRes {
  memberOverview: MemberOverview
}

export interface PersonalInstanceStat {
  unfinishedInstanceNum: number
  unfinishedInstanceLimit: number
}

export interface GetTeamStatPersonalInstancesRes {
  personalInstanceStat: PersonalInstanceStat
}

// Team Member Statistics - Shared User Type
export interface TeamMemberStatUser {
  resID: string
  user: {
    name: string
    role: number
    nickname: string
    note: string
    status: number
  }
}

// Team Member Statistics Responses
export interface GetTeamStatMemberInstancesRes {
  pagination: Pagination
  stats: Array<{
    user: TeamMemberStatUser
    unfinishedCount: number
    costFeeCent: number
    durationSecond: number
  }>
}

export interface GetTeamStatMemberSnapshotsRes {
  pagination: Pagination
  stats: Array<{
    user: TeamMemberStatUser
    availCount: number
    spaceQuotaBytes: number
    spaceUsageBytes: number
  }>
}

export interface GetTeamStatMemberFSSpacesRes {
  pagination: Pagination
  stats: Array<{
    user: TeamMemberStatUser
    availRegions: number
    spaceQuotaBytes: number
    spaceUsageBytes: number
  }>
}

export type PostGrantMonitorTokenRes = {
  code: number
  msg?: string
  token?: string
}

// Elastic IP
export type GetAvailableEIPsRes = Pagi<{
  eIPS: import('./type-server').ServerEIPInfo[]
}>

export type BuyEIPRes = {
  code: number
  msg: string
  resID: string
}

// ACL Responses
export type GetACLResourceRes = {
  resource: import('./type-server').ServerACLResource
}

export type CreateACLResourceRes = {
  resource?: import('./type-server').ServerACLResource
  resID?: string
}

export type CreateACLL3RuleRes = {
  l3RuleID: number
}

export type GetACLL3RulesRes = Pagi<{
  l3Rules: import('./type-server').ServerACLL3Rule[]
}>

export type UpdateACLL3RuleRes = {
  version: number
}

export type CreateACLL4ExceptionRuleRes = {
  l4RuleIDs: number[]
}

export type GetACLL4ExceptionRulesRes = Pagi<{
  l4Rules: import('./type-server').ServerACLL4Rule[]
}>

export type UpdateACLL4ExceptionRuleRes = {
  version: number
}

// Items
export interface ItemPrice {
  type: number
  priceCent: number
}

export interface StorageItemDesc {
  storage: {
    capacityMB: string
  }
}

export interface TeamQuotaLimit {
  type: number
  upperLimit: string
}

export interface TeamPlanLimit {
  membersNumLimit: string
  adminsNumLimit?: string
  sharedSnapshotLimit: string
  sharedSpaceLimit: string
  billingMode: number
  snapshotResumeLimit: number
  quotaLimits: TeamQuotaLimit[]
}

export interface TeamItemDesc {
  team: {
    plan?: number
    limit: TeamPlanLimit
    name?: string // Plan name: Free / Basic / Premium
  }
}

export interface BaseItem {
  id: number
  catalog: number
  paymentType: number
  prices: ItemPrice[]
  sourceID: number
  qty: number
  deleted: boolean
  domain: number
  resID: string
}

export interface StorageItem extends BaseItem {
  catalog: 1
  desc: StorageItemDesc
}

export interface TeamItem extends BaseItem {
  catalog: 8
  desc: TeamItemDesc
}

export type Item = StorageItem | TeamItem

export type GetItemsRes = Pagi<{
  items: Item[]
}>

// Instance Spec (Private Region) Responses
export type GetPrivateRegionAgentInstancesRes = {
  instances: ServerPrivateRegionAgentInstance[]
}

export type UpdatePrivateRegionNodeStatusRes = {
  code: number // 0: success, 19: invalid status, 204: exists unfinished instances, 208: region access denied
}

export type GetPrivateRegionVGpuSpecsRes = {
  specs: ServerPrivateRegionVGpuSpec[]
}

export type GetPrivateRegionNodeSpecsRes = {
  specs: ServerPrivateRegionNodeSpec[]
}

export type VerifyPrivateRegionSpecsRes = {
  code: number
  errors?: string[]
}

export type UpdatePrivateRegionSpecsRes = {
  code: number
}

// AK/SK Credential
export type CreateCredentialRes = {
  resID: string
  accessKeyEncoded: string
  secretKeyEncoded: string
}

export type GetCredentialRes = {
  credential: ServerCredentialInfo
}

export type GetCredentialListRes = Pagi<{
  credentials: ServerCredentialInfo[]
}>

export type RotateCredentialRes = {
  accessKeyEncoded: string
  secretKeyEncoded: string
}

export type CreateInstancePlanOrderRes = {
  code: number
  msg: string
  order?: {
    id: number
  }
}

export type SwitchOrderToPayAsYouGoRes = {
  code: number
  msg: string
}

export interface PurchaseItemRes {
  code: number
  msg: string
  detail?: any
}
